import React from 'react';
import PropTypes from 'prop-types';

// Reusable trade configuration card used for both Configuration1 and Configuration2
// Props:
// - title: card title
// - groupKey: identifier for the group
// - tradesList: array of original trade rows (used for indexing)
// - getTradeData: function(groupKey, index) => trade object
// - updateTradeField: function to update a trade field
// - addTradeRow: function(groupKey)
// - removeTradeRow: function(groupKey, index)
// - resetGroupTrades: optional function to reset
// - availableAccounts, symbolsByGroup, holdingsLookup, formatCurrency
// - groupAlloc: optional allocation object { beforeValue, beforeWeight, targetWeight, minTargetWeight, maxTargetWeight }
const TradeConfigurationCard = ({
  title,
  groupKey,
  tradesList = [],
  getTradeData,
  updateTradeField,
  addTradeRow,
  removeTradeRow,
  resetGroupTrades,
  availableAccounts = [],
  symbolsByGroup = {},
  holdingsLookup = {},
  formatCurrency = (v) => v,
  groupAlloc = null
}) => {
  return (
    <div className="card border">
      <div className="card-header bg-white py-2 px-3 d-flex justify-content-between align-items-center">
        <h6 className="mb-0 text-primary">{title}</h6>
        <div className="d-flex gap-2">
          {resetGroupTrades && (
            <button type="button" className="btn btn-sm btn-outline-secondary" onClick={() => resetGroupTrades(groupKey)}>
              <i className="bi bi-arrow-counterclockwise me-1"></i>Reset
            </button>
          )}
          {addTradeRow && (
            <button type="button" className="btn btn-sm btn-success" onClick={() => addTradeRow(groupKey)}>
              <i className="bi bi-plus-circle me-1"></i>Add Trade
            </button>
          )}
        </div>
      </div>
      <div className="card-body p-0">
        <div className="row align-items-center py-2 px-3 bg-light border-bottom g-0">
          <div className="col" style={{ flex: '0 0 5%', maxWidth: '5%', paddingLeft: 0, paddingRight: 0}}>
            <small className="text-muted fw-bold">Trade</small>
          </div>
          <div className="col" style={{ flex: '0 0 24%', maxWidth: '24%', paddingLeft: 0, paddingRight: 0 }}>
            <small className="text-muted fw-bold">Account</small>
          </div>
          <div className="col" style={{ flex: '0 0 16%', maxWidth: '16%', paddingLeft: 0, paddingRight: 0 }}>
            <small className="text-muted fw-bold">Symbol</small>
          </div>
          <div className="col" style={{ flex: '0 0 18%', maxWidth: '18%', paddingLeft: 0, paddingRight: 0 }}>
            <small className="text-muted fw-bold">Quantity (+Buy/-Sell)</small>
          </div>
          <div className="col" style={{ flex: '0 0 18%', maxWidth: '18%', paddingLeft: 0, paddingRight: 0 }}>
            <small className="text-muted fw-bold">Dollars (+Buy/-Sell)</small>
          </div>
          <div className="col text-center" style={{ flex: '0 0 7%', maxWidth: '7%', paddingLeft: 0, paddingRight: 0 }}>
            <small className="text-muted fw-bold">Price</small>
          </div>
          <div className="col text-center" style={{ flex: '0 0 7%', maxWidth: '7%', paddingLeft: 0, paddingRight: 0 }}>
            <small className="text-muted fw-bold">Action</small>
          </div>
          <div className="col text-center" style={{ flex: '0 0 5%', maxWidth: '5%', paddingLeft: 0, paddingRight: 0 }}>
            <small className="text-muted fw-bold">Delete</small>
          </div>
        </div>

        {(tradesList || []).map((orig, idx) => {
          const trade = getTradeData(groupKey, idx);
          const quantity = trade.quantity || 0;
          const price = trade.price || 0;
          const dollars = (quantity * price) || 0;
          // Use the stored action field — do NOT re-derive from quantity sign.
          // trade.action is kept in sync by updateTradeField and initialization.
          const storedAction = (trade.action || (quantity < 0 ? 'SELL' : 'BUY')).toUpperCase();
          const actionInfo = storedAction === 'SELL' ? { action: 'Sell', class: 'bg-danger' }
            : storedAction === 'BUY' ? { action: 'Buy', class: 'bg-success' }
            : { action: 'Hold', class: 'bg-secondary' };

          // Quick-fill calculations (only used when groupAlloc is provided)
          const bv = groupAlloc?.beforeValue || 0;
          const bw = groupAlloc?.beforeWeight || 0;
          const minW = groupAlloc?.minTargetWeight || 0;
          const tgtW = groupAlloc?.targetWeight || 0;
          const maxW = groupAlloc?.maxTargetWeight || 0;
          const totalPortVal = bw > 0 ? bv / (bw / 100) : 0;
          // Sum pending trade values from all OTHER rows with the same symbol so that
          // "buy/sell to weight" accounts for trades already entered in sibling rows.
          const pendingValueOtherRows = (tradesList || []).reduce((sum, _, i) => {
            if (i === idx) return sum;
            const other = getTradeData(groupKey, i);
            if (!other.symbol || other.symbol !== trade.symbol) return sum;
            const otherPrice = other.price || price;
            return sum + (other.quantity || 0) * otherPrice;
          }, 0);
          const adjustedBv = bv + pendingValueOtherRows;
          const deltaQty = (w) => totalPortVal > 0 && price > 0 ? ((totalPortVal * (w / 100)) - adjustedBv) / price : 0;
          const heldQty = holdingsLookup[`${trade.account_number || ''}-${trade.symbol || ''}`] || 0;
          const showQuickFill = !!(trade.symbol && price > 0 && groupAlloc && bw > 0);
          const qBtnStyle = { fontSize: '0.55rem', padding: '1px 4px' };

          return (
            <div
              key={`${trade.account_number || ''}-${trade.symbol || ''}-${idx}`}
              className="border-bottom"
            >
              <div className="row align-items-center py-2 px-3" style={{ backgroundColor: 'white' }}>
              <div className="col" style={{ flex: '0 0 5%', maxWidth: '5%' }}>
                <strong>
                  {idx + 1}
                </strong>
              </div>

              <div className="col" style={{ flex: '0 0 24%', maxWidth: '24%', paddingLeft: 0, paddingRight: 10 }}>
                <select className="form-select form-select-sm" style={{ margin: 0 }} value={trade.account_name || ''} onChange={(e) => {
                  const selected = availableAccounts.find(a => a.name === e.target.value);
                  updateTradeField(groupKey, idx, 'account_name', e.target.value);
                  if (selected) updateTradeField(groupKey, idx, 'account_number', selected.number);
                }}>
                  <option value="">Select Account</option>
                  {availableAccounts.map(a => <option key={a.number} value={a.name}>{a.name}</option>)}
                  {trade.account_name && !availableAccounts.find(a => a.name === trade.account_name) && (
                    <option value={trade.account_name}>{trade.account_name}</option>
                  )}
                </select>
              </div>

              <div className="col" style={{ flex: '0 0 16%', maxWidth: '16%', paddingLeft: 0, paddingRight: 10 }}>
                <select className="form-select form-select-sm" value={trade.symbol || ''} disabled={!trade.account_number} onChange={(e) => {
                    const sym = e.target.value;
                  const symbolInfo = (symbolsByGroup[groupKey] || []).find(s => s.symbol === sym);
                  updateTradeField(groupKey, idx, { symbol: sym, price: symbolInfo?.price || 0, security_type: symbolInfo?.security_type || 'EQUITY', trading_security_type: symbolInfo?.trading_security_type || '' });
                }}>
                  <option value="">Symbol</option>
                  {(symbolsByGroup[groupKey] || []).map(s => {
                    const lookupKey = `${trade.account_number}-${s.symbol}`;
                    const holdingsQty = holdingsLookup[lookupKey] || 0;
                    const display = holdingsQty > 0 ? `${s.symbol} (${holdingsQty.toLocaleString()})` : s.symbol;
                    return <option key={s.symbol} value={s.symbol}>{display}</option>;
                  })}
                  {trade.symbol && !(symbolsByGroup[groupKey] || []).find(s => s.symbol === trade.symbol) && (
                    <option value={trade.symbol}>{trade.symbol}</option>
                  )}
                </select>
              </div>

              <div className="col" style={{ flex: '0 0 18%', maxWidth: '18%', paddingLeft: 0, paddingRight: 10 }}>
                <div className="d-flex align-items-center">
                  <input type="number" className="form-control form-control-sm text-end" style={{ width: '100%' }} value={quantity} disabled={!trade.symbol} onChange={(e) => updateTradeField(groupKey, idx, 'quantity', parseFloat(e.target.value) || 0, orig)} step="1" />
                  <div className="d-flex align-items-center ms-1">
                    <div className="btn-group-vertical" role="group">
                      <div className="btn-group btn-group-sm" role="group">
                        <button type="button" className="btn btn-outline-secondary" style={{ fontSize: '0.55rem', padding: '1px 3px' }} disabled={!trade.symbol} onClick={() => updateTradeField(groupKey, idx, 'quantity', (trade.quantity || 0) + 10, orig)}>+10</button>
                        <button type="button" className="btn btn-outline-secondary" style={{ fontSize: '0.55rem', padding: '1px 3px' }} disabled={!trade.symbol} onClick={() => updateTradeField(groupKey, idx, 'quantity', (trade.quantity || 0) + 100, orig)}>+100</button>
                      </div>
                      <div className="btn-group btn-group-sm" role="group">
                        <button type="button" className="btn btn-outline-secondary" style={{ fontSize: '0.55rem', padding: '1px 3px' }} disabled={!trade.symbol} onClick={() => updateTradeField(groupKey, idx, 'quantity', (trade.quantity || 0) - 10, orig)}>-10</button>
                        <button type="button" className="btn btn-outline-secondary" style={{ fontSize: '0.55rem', padding: '1px 3px' }} disabled={!trade.symbol} onClick={() => updateTradeField(groupKey, idx, 'quantity', (trade.quantity || 0) - 100, orig)}>-100</button>
                      </div>
                    </div>
                    <button type="button" className="btn btn-outline-primary btn-sm ms-1" style={{ fontSize: '0.55rem', padding: '4px 4px' }} disabled={!trade.symbol} onClick={() => updateTradeField(groupKey, idx, 'quantity', Math.round(trade.quantity || 0), orig)}>Round</button>
                  </div>
                </div>
              </div>

              <div className="col" style={{ flex: '0 0 18%', maxWidth: '18%', paddingLeft: 0, paddingRight: 10 }}>
                <div className="d-flex align-items-center">
                  <input type="number" className="form-control form-control-sm text-end" style={{ width: '100%' }} value={dollars.toFixed(2)} disabled={!trade.symbol} onChange={(e) => { const newD = parseFloat(e.target.value) || 0; if (price > 0) updateTradeField(groupKey, idx, 'quantity', newD / price, orig); }} step="100" />
                  <div className="d-flex align-items-center ms-1">
                    <div className="btn-group-vertical" role="group">
                      <div className="btn-group btn-group-sm" role="group">
                        <button type="button" className="btn btn-outline-secondary" style={{ fontSize: '0.55rem', padding: '1px 3px' }} disabled={!trade.symbol} onClick={() => { if (price > 0) updateTradeField(groupKey, idx, 'quantity', (trade.quantity || 0) + (100 / price), orig); }}>+$100</button>
                        <button type="button" className="btn btn-outline-secondary" style={{ fontSize: '0.55rem', padding: '1px 3px' }} disabled={!trade.symbol} onClick={() => { if (price > 0) updateTradeField(groupKey, idx, 'quantity', (trade.quantity || 0) + (1000 / price), orig); }}>+$1K</button>
                      </div>
                      <div className="btn-group btn-group-sm" role="group">
                        <button type="button" className="btn btn-outline-secondary" style={{ fontSize: '0.55rem', padding: '1px 3px' }} disabled={!trade.symbol} onClick={() => { if (price > 0) updateTradeField(groupKey, idx, 'quantity', (trade.quantity || 0) - (100 / price), orig); }}>-$100</button>
                        <button type="button" className="btn btn-outline-secondary" style={{ fontSize: '0.55rem', padding: '1px 3px' }} disabled={!trade.symbol} onClick={() => { if (price > 0) updateTradeField(groupKey, idx, 'quantity', (trade.quantity || 0) - (1000 / price), orig); }}>-$1K</button>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              <div className="col text-center" style={{ flex: '0 0 7%', maxWidth: '7%' }}>
                <small>{formatCurrency(price)}</small>
              </div>

              <div className="col text-center" style={{ flex: '0 0 7%', maxWidth: '7%' }}>
                <span className={`badge ${actionInfo.class}`} style={{ fontSize: '0.8rem' }}>{actionInfo.action}</span>
              </div>

              <div className="col text-center" style={{ flex: '0 0 5%', maxWidth: '5%' }}>
                <button type="button" className="btn btn-sm btn-outline-danger" title="Remove this trade" style={{ fontSize: '0.7rem', padding: '1px 4px' }} onClick={() => removeTradeRow(groupKey, idx)}>
                  <i className="bi bi-trash"></i>
                </button>
              </div>
              </div>

              {/* Quick-fill shortcuts — only shown when symbol is selected and allocation data is available */}
              {showQuickFill && (
                <div className="d-flex align-items-center flex-wrap px-3 pb-2 gap-1" style={{ backgroundColor: '#f8f9fa', borderTop: '1px solid #e9ecef' }}>
                  <small className="text-muted fw-bold me-1" style={{ fontSize: '0.6rem' }}>Quick Fill:</small>

                  {/* Sell shortcuts */}
                  <span className="text-danger fw-bold" style={{ fontSize: '0.6rem' }}>Sell:</span>
                  <button type="button" className="btn btn-outline-danger btn-sm" style={qBtnStyle}
                    disabled={heldQty <= 0}
                    onClick={() => updateTradeField(groupKey, idx, 'quantity', -heldQty)}
                    title={`Sell all held shares (${heldQty.toLocaleString()})`}>
                    All Qty
                  </button>
                  {maxW > 0 && (
                    <button type="button" className="btn btn-outline-danger btn-sm" style={qBtnStyle}
                      onClick={() => updateTradeField(groupKey, idx, 'quantity', Math.min(0, deltaQty(maxW)))}
                      title={`Sell to max weight (${maxW.toFixed(2)}%)`}>
                      →Max
                    </button>
                  )}
                  {tgtW > 0 && (
                    <button type="button" className="btn btn-outline-danger btn-sm" style={qBtnStyle}
                      onClick={() => updateTradeField(groupKey, idx, 'quantity', Math.min(0, deltaQty(tgtW)))}
                      title={`Sell to target weight (${tgtW.toFixed(2)}%)`}>
                      →Tgt
                    </button>
                  )}
                  {minW > 0 && (
                    <button type="button" className="btn btn-outline-danger btn-sm" style={qBtnStyle}
                      onClick={() => updateTradeField(groupKey, idx, 'quantity', Math.min(0, deltaQty(minW)))}
                      title={`Sell to min weight (${minW.toFixed(2)}%)`}>
                      →Min
                    </button>
                  )}

                  <div className="vr mx-1" style={{ height: '16px' }} />

                  {/* Buy shortcuts */}
                  <span className="text-success fw-bold" style={{ fontSize: '0.6rem' }}>Buy:</span>
                  {minW > 0 && (
                    <button type="button" className="btn btn-outline-success btn-sm" style={qBtnStyle}
                      onClick={() => updateTradeField(groupKey, idx, 'quantity', Math.max(0, deltaQty(minW)))}
                      title={`Buy to min weight (${minW.toFixed(2)}%)`}>
                      →Min
                    </button>
                  )}
                  {tgtW > 0 && (
                    <button type="button" className="btn btn-outline-success btn-sm" style={qBtnStyle}
                      onClick={() => updateTradeField(groupKey, idx, 'quantity', Math.max(0, deltaQty(tgtW)))}
                      title={`Buy to target weight (${tgtW.toFixed(2)}%)`}>
                      →Tgt
                    </button>
                  )}
                  {maxW > 0 && (
                    <button type="button" className="btn btn-outline-success btn-sm" style={qBtnStyle}
                      onClick={() => updateTradeField(groupKey, idx, 'quantity', Math.max(0, deltaQty(maxW)))}
                      title={`Buy to max weight (${maxW.toFixed(2)}%)`}>
                      →Max
                    </button>
                  )}
                  <button type="button" className="btn btn-outline-success btn-sm" style={qBtnStyle}
                    disabled={maxW <= 0}
                    onClick={() => updateTradeField(groupKey, idx, 'quantity', Math.max(0, deltaQty(maxW)))}
                    title={`Buy all (to max target weight ${maxW.toFixed(2)}%)`}>
                    All $
                  </button>
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
};

TradeConfigurationCard.propTypes = {
  title: PropTypes.string,
  groupKey: PropTypes.string.isRequired,
  tradesList: PropTypes.array,
  getTradeData: PropTypes.func.isRequired,
  updateTradeField: PropTypes.func.isRequired,
  addTradeRow: PropTypes.func,
  removeTradeRow: PropTypes.func,
  resetGroupTrades: PropTypes.func,
  availableAccounts: PropTypes.array,
  symbolsByGroup: PropTypes.object,
  holdingsLookup: PropTypes.object,
  formatCurrency: PropTypes.func,
  groupAlloc: PropTypes.object
};

export default TradeConfigurationCard;
